import { memo } from 'react';

import { ProfileNonToxicIcon } from '@/features/profile/components/profileNonToxicIcon';
import {
  useCloseModal,
  useRemoveModal,
} from '@/shared/components/protectedModals/atoms/modalStack';
import type { ModalStackItem } from '@/shared/components/protectedModals/atoms/modalStack/types';
import { Button } from '@/shared/components/ui/button';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/shared/components/ui/custom/responsive-dialog';

import type { ProfileNonToxicModalData } from './atoms/profileNonToxicModal';
import { ProfileNonToxicGuidelines } from './nonToxicGuidelines';
import { ProfileNonToxicTier } from './nonToxicTier';

const transformations = [
  {
    width: 60,
    height: 60,
  },
];

export interface ProfileNonToxicModalProps extends ModalStackItem {
  data: ProfileNonToxicModalData;
  type: 'non-toxic-modal';
}

export const ProfileNonToxicModal = memo(function ProfileNonToxicModal(
  props: ProfileNonToxicModalProps,
) {
  const { id, isOpen, data } = props;
  const closeModal = useCloseModal();
  const removeModal = useRemoveModal();

  return (
    <ResponsiveDialog
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (!open) {
          closeModal(id);
        }
      }}
      onAnimationEnd={(open: boolean) => {
        if (!open) {
          removeModal(id);
        }
      }}
    >
      <ResponsiveDialogContent>
        <ResponsiveDialogHeader className="flex flex-col items-center gap-0.5">
          <ProfileNonToxicIcon
            className="size-15"
            transformations={transformations}
            tier={data.tier}
          />
          <div className="flex w-full flex-col items-center justify-center gap-1.5">
            <ResponsiveDialogTitle className="text-card-foreground text-lg leading-7 font-semibold">
              Stay safe. play fair. level up.
            </ResponsiveDialogTitle>
            {data.recentFeedback && (
              <p className="text-sm leading-5 font-medium">
                <span className="text-cold-400">Your stats (last 90d): </span>
                <span className="text-white">
                  {data.recentFeedback.totalCount} reviews &{' '}
                  {Math.round(data.recentFeedback.positiveRatio * 100)}%
                  positive
                </span>
              </p>
            )}
          </div>
        </ResponsiveDialogHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
          <ProfileNonToxicTier tier={data.tier} />
          <ProfileNonToxicGuidelines />
        </div>
        <ResponsiveDialogFooter>
          <Button
            variant="default"
            onClick={() => {
              closeModal(id);
            }}
          >
            Got it 👍
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
});
